import type React from 'react';
import { Alignment, Direction, Media } from './constants';

export type CSSObject = React.CSSProperties;

// ── Column filter types ────────────────────────────────────────────────────────

export type FilterType = 'text' | 'number' | 'date' | 'datetime' | 'time';

export type FilterOperator =
	| 'contains'
	| 'notContains'
	| 'equals'
	| 'notEquals'
	| 'startsWith'
	| 'endsWith'
	| 'blank'
	| 'notBlank'
	| 'gt'
	| 'gte'
	| 'lt'
	| 'lte'
	| 'between'
	| 'before'
	| 'after';

export type FilterCondition = {
	operator: FilterOperator;
	value?: string;
	/** Second value — only used when operator is "between". */
	value2?: string;
};

export type FilterState = {
	condition1: FilterCondition;
	condition2?: FilterCondition;
	/** How condition1 and condition2 combine. Defaults to "AND". */
	logic?: 'AND' | 'OR';
};

export enum SortOrder {
	ASC = 'asc',
	DESC = 'desc',
}

/** A single column participating in a (possibly multi-column) sort, in priority order. */
export type SortColumn<T> = {
	column: TableColumn<T>;
	sortDirection: SortOrder;
};

export type Primitive = string | number | boolean | bigint | Date;
export type ColumnSortFunction<T> = (a: T, b: T) => number;
export type ExpandRowToggled<T> = (expanded: boolean, row: T) => void;
export type Format<T> = (row: T, rowIndex: number) => React.ReactNode;
export type RowState<T> = ((row: T) => boolean) | null;
export type Selector<T> = (row: T, rowIndex?: number) => Primitive;
export type SortFunction<T> = (rows: T[], field: Selector<T>, sortDirection: SortOrder) => T[];
export type TableRow = Record<string, unknown>;
export type ComponentProps = Record<string, unknown>;
export type ExpanderComponentProps<T> = { data: T };
export type ExpandableRowsComponent<T> = React.ComponentType<ExpanderComponentProps<T>>;
export type PaginationChangePage = (page: number, totalRows: number) => void;
export type PaginationChangeRowsPerPage = (currentRowsPerPage: number, currentPage: number) => void;
export type PaginationComponentProps = {
	rowsPerPage: number;
	rowCount: number;
	currentPage: number;
	onChangePage: PaginationChangePage;
	onChangeRowsPerPage: PaginationChangeRowsPerPage;
	direction?: Direction;
	paginationRowsPerPageOptions?: number[];
	paginationIcons?: PaginationIcons;
	paginationComponentOptions?: PaginationOptions;
	localization?: Localization['pagination'];
};
export type PaginationComponent = React.ComponentType<PaginationComponentProps>;

/** Renders a column's footer cell. Either a static node, or a function that
 *  receives the filtered+sorted rows for the current view and returns a node
 *  (typically an aggregate such as a sum or average). */
export type ColumnFooter<T> = React.ReactNode | ((rows: T[]) => React.ReactNode);

/** Props passed to a custom `footerComponent`. */
export type FooterComponentProps<T> = {
	/** Filtered + sorted rows in the current view. With server-side pagination
	 *  this is whatever data the parent passed in — page-bound, not the full dataset. */
	rows: T[];
	/** The column definitions in their current visible order. */
	columns: TableColumn<T>[];
};

export type FooterComponent<T> = React.ComponentType<FooterComponentProps<T>>;

export type DataTableHandle = {
	clearSelectedRows: () => void;
	clearSort: () => void;
};

// ── Context menu types ────────────────────────────────────────────────────────

export type ContextMenuTrigger = 'right-click' | 'menu-button' | 'both';

/** Which inline edge the row menu button sits on. Logical (RTL-aware): `'end'` is
 *  the row's inline end (right in LTR), `'start'` the inline start (left in LTR). */
export type ContextMenuPosition = 'start' | 'end';

export type ContextMenuConfig = {
	/** Enable the header context menu. Defaults to `true`. */
	header?: boolean;
	/** Enable the row context menu. Only opens when `contextMenuActions.row` returns items. Defaults to `true`. */
	row?: boolean;
	/** How the menu opens. Defaults to `'right-click'`. */
	trigger?: ContextMenuTrigger;
	/** Which inline edge the row menu button sits on. Defaults to `'end'`. */
	menuPosition?: ContextMenuPosition;
};

/** A single item in a context menu. Built-in header actions use the reserved ids
 *  `sort-asc`, `sort-desc`, `clear-sort`, `pin-left`, `pin-right`, `unpin`,
 *  `hide-column`, and `reset`. */
export type ContextMenuAction = {
	id: string;
	label: React.ReactNode;
	disabled?: boolean;
	/** Optional icon rendered before the label. */
	icon?: React.ReactNode;
};

/** What the selected menu action applies to. */
export type ContextMenuActionContext<T> =
	{ type: 'header'; column: TableColumn<T> } | { type: 'row'; row: T; rowIndex: number };

export type ContextMenuActions<T> = {
	/** Extra header menu items, appended after the built-ins. A static list or a function of the column. */
	header?: ContextMenuAction[] | ((column: TableColumn<T>) => ContextMenuAction[]);
	/** Row menu items. The row menu renders only these — there are no built-in row actions. */
	row?: (row: T, rowIndex: number) => ContextMenuAction[];
};

// ── Feature-group prop types ──────────────────────────────────────────────────

type SelectionProps<T> = {
	/** @deprecated Use a ref with DataTableHandle instead: ref.current.clearSelectedRows() */
	clearSelectedRows?: boolean;
	onSelectedRowsChange?: (selected: { allSelected: boolean; selectedCount: number; selectedRows: T[] }) => void;
	selectableRowDisabled?: RowState<T>;
	selectableRows?: boolean;
	selectableRowsComponent?: 'input' | React.ComponentType<React.InputHTMLAttributes<HTMLInputElement>>;
	selectableRowsComponentProps?: ComponentProps;
	selectableRowSelected?: RowState<T>;
	selectableRowsHighlight?: boolean;
	selectableRowsNoSelectAll?: boolean;
	selectableRowsVisibleOnly?: boolean;
	selectableRowsSingle?: boolean;
	/**
	 * Controlled selection. When provided, DataTable uses this array as the source of truth
	 * for which rows are selected and calls `onSelectedRowsChange` whenever the user toggles a row,
	 * a range, or the select-all checkbox. Omit to use internal selection state (default).
	 *
	 * Rows are matched by `keyField`, so each row in `selectedRows` must contain its key.
	 */
	selectedRows?: T[];
	/**
	 * Enable Shift-click range selection on the row checkbox. When the user clicks one row's
	 * checkbox, then Shift-clicks another, every row in between is toggled to match the anchor.
	 * Ignored when `selectableRowsSingle` is true. Defaults to `true`.
	 */
	selectableRowsRange?: boolean;
};

export interface PaginationIcons {
	first?: React.ReactNode;
	last?: React.ReactNode;
	next?: React.ReactNode;
	previous?: React.ReactNode;
}

type PaginationProps = {
	onChangePage?: PaginationChangePage;
	onChangeRowsPerPage?: PaginationChangeRowsPerPage;
	pagination?: boolean;
	paginationComponent?: PaginationComponent;
	paginationComponentOptions?: PaginationOptions;
	paginationDefaultPage?: number;
	/**
	 * @deprecated Pass via the theme instead: `createTheme('t', { icons: { pagination: { next: <Icon /> } } })`
	 */
	paginationIcons?: PaginationIcons;
	paginationPerPage?: number;
	paginationPage?: number;
	paginationPosition?: 'top' | 'bottom' | 'both';
	paginationResetDefaultPage?: boolean;
	paginationRowsPerPageOptions?: number[];
	paginationServer?: boolean;
	paginationServerOptions?: PaginationServerOptions;
	paginationTotalRows?: number;
};

type ExpandableProps<T> = {
	/**
	 * @deprecated Pass via the theme instead: `createTheme('t', { icons: { expandable: { collapsed: <Icon /> } } })`
	 */
	expandableIcon?: ExpandableIcon;
	expandableInheritConditionalStyles?: boolean;
	expandableRowDisabled?: RowState<T>;
	expandableRowExpanded?: RowState<T>;
	expandableRows?: boolean;
	expandableRowsComponent?: ExpandableRowsComponent<T>;
	expandableRowsComponentProps?: ComponentProps;
	expandableRowsHideExpander?: boolean;
	expandOnRowClicked?: boolean;
	expandOnRowDoubleClicked?: boolean;
	onRowExpandToggled?: ExpandRowToggled<T>;
	/**
	 * @deprecated Use the `localization` prop instead: `localization={{ expandable: { ... } }}`. Will be removed in v9.
	 */
	expandableRowsOptions?: ExpandableRowsOptions;
};

type SortProps<T> = {
	defaultSortAsc?: boolean;
	defaultSortFieldId?: string | number | null | undefined;
	onSort?: (
		selectedColumn: TableColumn<T>,
		sortDirection: SortOrder,
		sortedRows: T[],
		sortColumns: SortColumn<T>[],
	) => void;
	sortFunction?: SortFunction<T> | null;
	/**
	 * @deprecated Pass via the theme instead: `createTheme('t', { icons: { sort: <Icon /> } })`
	 */
	sortIcon?: React.ReactNode;
	sortServer?: boolean;
	/**
	 * Enable multi-column sorting. Hold Ctrl (or ⌘ on macOS) while clicking a column
	 * header to add it to the existing sort instead of replacing it. Sort priority follows
	 * the order columns are added. Defaults to `false`.
	 */
	sortMulti?: boolean;
};

type BaseTableProps<T> = {
	actions?: React.ReactNode | React.ReactNode[];
	ariaLabel?: string;
	className?: string;
	columns: TableColumn<T>[];
	conditionalRowStyles?: ConditionalStyles<T>[];
	customStyles?: TableStyles;
	data: T[];
	dense?: boolean;
	direction?: Direction;
	disabled?: boolean;
	fixedHeader?: boolean;
	fixedHeaderScrollHeight?: string;
	highlightOnHover?: boolean;
	keyField?: keyof T & string;
	noDataComponent?: React.ReactNode;
	noHeader?: boolean;
	noTableHead?: boolean;
	onRowClicked?: (row: T, e: React.MouseEvent) => void;
	onRowDoubleClicked?: (row: T, e: React.MouseEvent) => void;
	onRowMiddleClicked?: (row: T, e: React.MouseEvent) => void;
	onRowMouseEnter?: (row: T, e: React.MouseEvent) => void;
	onRowMouseLeave?: (row: T, e: React.MouseEvent) => void;
	/** Called when the user scrolls the table body. Works with both `fixedHeader` enabled and disabled. */
	onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
	/** Enable drag-to-resize handles on column headers */
	resizable?: boolean;
	/**
	 * Seed column widths (px) by column id — useful for hydrating persisted widths on mount.
	 * Keyed by the same id you set on each TableColumn.
	 */
	initialColumnWidths?: Record<string | number, number>;
	/**
	 * Called after the user finishes resizing a column (on mouseup).
	 * Receives the id and final width (px) of the resized column, plus the full widths map
	 * so you can persist to localStorage, a database, or anywhere else.
	 */
	onColumnResize?: (columnId: string | number, width: number, allWidths: Record<string | number, number>) => void;
	/**
	 * Enable spreadsheet-style keyboard navigation between cells. The table renders as a
	 * WAI-ARIA grid (`role="grid"`, single Tab stop, roving tabindex): arrow keys move
	 * between cells — including selection checkboxes, expander buttons, and the header
	 * row — Home/End jump to the row edges, and Ctrl+Home/Ctrl+End jump to the grid
	 * corners. Enter or F2 opens the focused cell's editor if it has one
	 * (`editable`/`editor`) and Escape cancels it; Enter or Space on a header sorts the
	 * column. Navigation is clamped to the current page. Defaults to `false`.
	 */
	cellNavigation?: boolean;
	/** Animate rows on mount and expander rows on expand (respects prefers-reduced-motion) */
	animateRows?: boolean;
	/**
	 * Draw a vertical separator between body row columns.
	 * - `true` / `"subtle"`: inset 60%-height line.
	 * - `"full"`: full-height line using the theme divider color.
	 * - `false` / omitted: no body separators.
	 */
	columnSeparator?: boolean | 'subtle' | 'full';
	/**
	 * Draw a vertical separator between column header cells.
	 * Defaults to `true` (subtle inset line). Set to `false` to remove,
	 * or `"full"` for a full-height line.
	 * - `true` / `"subtle"` / omitted: inset 60%-height line (default).
	 * - `"full"`: full-height line using the theme divider color.
	 * - `false`: no header separators.
	 */
	headerSeparator?: boolean | 'subtle' | 'full';
	/** Column group definitions — renders a spanning header row above the column header row */
	columnGroups?: ColumnGroup[];
	/**
	 * Controlled filter values per column id. When provided, DataTable uses these values
	 * and calls onFilterChange when the user applies a filter. Omit to use internal filter state.
	 */
	filterValues?: Record<string | number, FilterState>;
	/** Called when the user clicks Apply in a column filter popup. */
	onFilterChange?: (columnId: string | number, filter: FilterState) => void;
	/** Override every user-visible string in DataTable. Pass a pre-built locale or build your own. */
	localization?: Localization;
	/**
	 * Enable a context menu on header cells and rows. Pass `true` to enable both with the
	 * default right-click trigger, or a config object to choose surfaces and trigger
	 * (`'right-click' | 'menu-button' | 'both'`). The header menu ships built-in actions
	 * (sort, pin, hide, reset); the row menu renders the items returned by
	 * `contextMenuActions.row` and stays closed when there are none.
	 */
	contextMenu?: boolean | ContextMenuConfig;
	/** Consumer-supplied menu items. Header items are appended after the built-ins. */
	contextMenuActions?: ContextMenuActions<T>;
	/** Called when any menu item is selected — built-ins included, after their effect is applied. */
	onContextMenuAction?: (action: ContextMenuAction, ctx: ContextMenuActionContext<T>) => void;
	/**
	 * @deprecated Use the `localization` prop instead: `localization={{ filter: { ... } }}`. Will be removed in v9.
	 */
	columnFilterOptions?: ColumnFilterOptions;
	onColumnOrderChange?: (nextOrder: TableColumn<T>[]) => void;
	/** Called after a group drag reorder with the new group order and matching column order. */
	onColumnGroupOrderChange?: (nextGroups: ColumnGroup[], nextColumns: TableColumn<T>[]) => void;
	persistTableHead?: boolean;
	pointerOnHover?: boolean;
	progressComponent?: React.ReactNode;
	progressPending?: boolean;
	progressSkeleton?: boolean;
	responsive?: boolean;
	striped?: boolean;
	style?: CSSObject;
	/** Content rendered in the sub-header bar. Providing any value shows the bar. */
	subHeader?: React.ReactNode;
	subHeaderAlign?: Alignment;
	subHeaderWrap?: boolean;
	theme?: ThemeProp;
	/** 'light' | 'dark' | 'system'. When 'system', the table auto-detects from prefers-color-scheme and the html.dark class. Default: 'light'. */
	colorMode?: ColorMode;
	/**
	 *  Shows and displays a header with a title
	 *  */
	title?: string | React.ReactNode;
	/**
	 * Replace the built-in footer row with a custom component. Receives `rows`
	 * (filtered + sorted) and `columns`. When omitted, the footer row is built
	 * from each column's `footer` field.
	 */
	footerComponent?: FooterComponent<T>;
	/**
	 * Force the footer row to render. By default the footer renders only when
	 * `footerComponent` is set or at least one visible column defines a `footer`.
	 * Set to `false` to suppress the footer entirely.
	 */
	showFooter?: boolean;
};

export type TableProps<T> = BaseTableProps<T> & SelectionProps<T> & PaginationProps & ExpandableProps<T> & SortProps<T>;

export type TableColumnBase = {
	allowOverflow?: boolean;
	button?: boolean;
	center?: boolean;
	compact?: boolean;
	reorder?: boolean;
	grow?: number;
	hide?: Media;
	id?: string | number;
	ignoreRowClick?: boolean;
	maxWidth?: string;
	minWidth?: string;
	name?: string | number | React.ReactNode;
	omit?: boolean;
	right?: boolean;
	sortable?: boolean;
	/** Enable the built-in filter popup for this column. */
	filterable?: boolean;
	/** Filter input type. Determines the available operators and input widget. Defaults to "text". */
	filterType?: FilterType;
	style?: CSSObject;
	width?: string;
	wrap?: boolean;
	/** Freeze this column to the left or right edge on horizontal scroll. */
	pinned?: 'left' | 'right';
	/** Allow clicking the cell to edit its value inline. Calls onCellEdit on commit.
	 *  Shorthand for `editor: { type: 'text' }`. Ignored when `editor` is set. */
	editable?: boolean;
	/** Configures the inline editor used when the cell is clicked. */
	editor?: CellEditor;
};

/** Context passed to a custom editor's render function. */
export interface CustomCellEditorContext<T = unknown> {
	/** The current row being edited. */
	row: T;
	/** Current editor value (string). Custom editors stay in sync via `setValue`. */
	value: string;
	/** Update the in-flight editor value without committing. */
	setValue: (next: string) => void;
	/** Commit the edit. If a value is passed it is used; otherwise the current value is committed. */
	commit: (value?: string) => void;
	/** Close the editor without firing onCellEdit. */
	cancel: () => void;
	/** The column definition. */
	column: TableColumn<T>;
	/** Current validation error from `validate`, or `null`. Set when a commit was rejected with a message. */
	error: string | null;
	/** Attach as `ref` on your focusable element to opt in to auto-focus on open and refocus after a rejected commit. */
	inputRef: React.RefCallback<HTMLElement>;
}

/** Options for inline cell editors. */
export type CellEditor<T = unknown> =
	| { type: 'text'; placeholder?: string }
	| { type: 'number'; placeholder?: string; min?: number; max?: number; step?: number }
	| { type: 'date'; min?: string; max?: string }
	| { type: 'checkbox' }
	| {
			type: 'select';
			options: Array<{ value: string; label: React.ReactNode }>;
			/** Optional placeholder shown when current value is empty/unknown. */
			placeholder?: string;
	  }
	| {
			type: 'custom';
			/** Render your own editor element. Use `ctx.commit()` / `ctx.cancel()` to finish. */
			render: (ctx: CustomCellEditorContext<T>) => React.ReactNode;
	  };

/**
 * Validate result returned from a column's `validate` function.
 * - `true`: accept the edit.
 * - `false`: reject silently.
 * - `string`: reject and surface the message as an inline validation error.
 */
export type CellValidateResult = true | false | string;

export type CellEditCallback<T> = (row: T, value: string, column: TableColumn<T>) => void;

export interface TableColumn<T> extends TableColumnBase {
	name?: string | number | React.ReactNode;
	sortField?: string;
	cell?: (row: T, rowIndex: number, column: TableColumn<T>, id: string | number) => React.ReactNode;
	conditionalCellStyles?: ConditionalStyles<T>[];
	format?: Format<T> | undefined;
	/**
	 * Extracts a value from the row for display and sorting.
	 * May return `React.ReactNode` when used for display only; if the column is sortable
	 * and no `sortFunction` is provided, the returned value should be a `Primitive`.
	 */
	selector?: (row: T, rowIndex?: number) => Primitive | React.ReactNode;
	sortFunction?: ColumnSortFunction<T>;
	/** Custom filter function — overrides built-in operator logic. Receives the full FilterState so both conditions are available. */
	filterFunction?: (row: T, filter: FilterState) => boolean;
	/** Called when the user commits an inline edit (blur or Enter). Only fires when editable: true. */
	onCellEdit?: CellEditCallback<T>;
	/**
	 * Validate an inline-edit value before `onCellEdit` fires.
	 * Return `true` to accept, `false` to reject silently, or a string error to display.
	 */
	validate?: (value: string, row: T, column: TableColumn<T>) => CellValidateResult;
	/**
	 * Footer cell for this column. Pass a `ReactNode` for a static value, or a function
	 * `(rows) => ReactNode` to compute an aggregate from the filtered+sorted rows.
	 * When any visible column defines a `footer`, a footer row renders below the body.
	 */
	footer?: ColumnFooter<T>;
}

/** A column group renders as a spanning header row above the regular header row. */
export interface ColumnGroup {
	/** Display name for this group header */
	name: string | React.ReactNode;
	/** The column ids that fall under this group */
	columnIds: (string | number)[];
	/** Horizontal alignment of the group label. Defaults to 'center'. */
	align?: 'left' | 'center' | 'right';
	/** Enable drag-to-reorder for this group. Dragging moves all member columns as a block. */
	reorder?: boolean;
}

export interface ConditionalStyles<T> {
	when: (row: T) => boolean;
	style?: CSSObject | ((row: T) => CSSObject);
	classNames?: string[];
}

export interface TableStyles {
	table?: {
		style: CSSObject;
	};
	tableWrapper?: {
		style: CSSObject;
	};
	responsiveWrapper?: {
		style: CSSObject;
	};
	header?: {
		style: CSSObject;
		fontColor?: string;
		fontSize?: string;
	};
	subHeader?: {
		style: CSSObject;
	};
	head?: {
		style: CSSObject;
	};
	headRow?: {
		style?: CSSObject;
		denseStyle?: CSSObject;
	};
	headCells?: {
		style?: CSSObject;
		draggingStyle?: CSSObject;
	};
	cells?: {
		style: CSSObject;
		draggingStyle?: CSSObject;
	};
	rows?: {
		style?: CSSObject;
		selectedHighlightStyle?: CSSObject;
		denseStyle?: CSSObject;
		highlightOnHoverStyle?: CSSObject;
		stripedStyle?: CSSObject;
	};
	expanderRow?: {
		style: CSSObject;
	};
	expanderCell?: {
		style: CSSObject;
	};
	expanderButton?: {
		style: CSSObject;
	};
	pagination?: {
		style?: CSSObject;
		pageButtonsStyle?: CSSObject;
	};
	footer?: {
		style?: CSSObject;
	};
	footerCells?: {
		style?: CSSObject;
	};
	noData?: {
		style: CSSObject;
	};
	progress?: {
		style: CSSObject;
	};
}

export interface PaginationOptions {
	noRowsPerPage?: boolean;
	rowsPerPageText?: string;
	rangeSeparatorText?: string;
	selectAllRowsItem?: boolean;
	selectAllRowsItemText?: string;
}

/**
 * @deprecated Use `Localization['filter']` instead. Will be removed in v9.
 */
export type ColumnFilterOptions = NonNullable<Localization['filter']>;

/**
 * @deprecated Use `Localization['expandable']` instead. Will be removed in v9.
 */
export type ExpandableRowsOptions = NonNullable<Localization['expandable']>;

/** All user-visible strings rendered by DataTable, grouped by feature area. */
export interface Localization {
	pagination?: {
		/** aria-label for the pagination nav element. Default: "Table pagination" */
		navigationAriaLabel?: string;
		/** aria-label for the First Page button. Default: "First Page" */
		firstPageAriaLabel?: string;
		/** aria-label for the Previous Page button. Default: "Previous Page" */
		previousPageAriaLabel?: string;
		/** aria-label for the Next Page button. Default: "Next Page" */
		nextPageAriaLabel?: string;
		/** aria-label for the Last Page button. Default: "Last Page" */
		lastPageAriaLabel?: string;
	};
	filter?: {
		/** aria-label for the filter icon button when no filter is active. Default: "Filter column" */
		filterColumnAriaLabel?: string;
		/** aria-label for the filter icon button when a filter is active. Default: "Filter active" */
		filterActiveAriaLabel?: string;
		/** aria-label for the filter panel dialog. Default: "Column filter" */
		filterPanelAriaLabel?: string;
		/** aria-label for the operator select element. Default: "Filter operator" */
		operatorAriaLabel?: string;
		/** Placeholder for the first value input. Default: "Value" */
		valuePlaceholder?: string;
		/** aria-label for the first value input. Default: "Filter value" */
		valueAriaLabel?: string;
		/** Placeholder for the second value input. Default: "Value" */
		value2Placeholder?: string;
		/** aria-label for the second value input (between operator). Default: "Filter second value" */
		value2AriaLabel?: string;
		/** Separator text between the two inputs for the "between" operator. Default: "and" */
		betweenSeparatorText?: string;
		/** aria-label for the remove-condition button. Default: "Remove condition" */
		removeConditionAriaLabel?: string;
		/** aria-label for the add-condition button. Default: "Add a second filter condition" */
		addConditionAriaLabel?: string;
		/** Label for the add-condition button. Default: "+ Add condition" */
		addConditionLabel?: string;
		/** Label for the Clear button. Default: "Clear" */
		clearLabel?: string;
		/** Label for the Apply button. Default: "Apply" */
		applyLabel?: string;
		/** Label for the AND logic button. Default: "AND" */
		andLabel?: string;
		/** Label for the OR logic button. Default: "OR" */
		orLabel?: string;
		/** Labels for filter operators. */
		operators?: {
			contains?: string;
			notContains?: string;
			equals?: string;
			notEquals?: string;
			startsWith?: string;
			endsWith?: string;
			blank?: string;
			notBlank?: string;
			gt?: string;
			gte?: string;
			lt?: string;
			lte?: string;
			between?: string;
			before?: string;
			after?: string;
		};
	};
	expandable?: {
		/** aria-label for the expand button. Default: "Expand Row" */
		expandRowAriaLabel?: string;
		/** aria-label for the collapse button. Default: "Collapse Row" */
		collapseRowAriaLabel?: string;
	};
	contextMenu?: {
		/** aria-label for the header menu. Default: "Column menu" */
		headerMenuAriaLabel?: string;
		/** aria-label for the row menu. Default: "Row menu" */
		rowMenuAriaLabel?: string;
		/** aria-label for the header menu (⋮) button. Default: "Column actions" */
		headerMenuButtonAriaLabel?: string;
		/** aria-label for the row menu (⋮) button. Default: "Row actions" */
		rowMenuButtonAriaLabel?: string;
		/** Label for the sort-ascending item. Default: "Sort ascending" */
		sortAscLabel?: string;
		/** Label for the sort-descending item. Default: "Sort descending" */
		sortDescLabel?: string;
		/** Label for the clear-sort item. Default: "Clear sort" */
		clearSortLabel?: string;
		/** Label for the pin-left item. Default: "Pin left" */
		pinLeftLabel?: string;
		/** Label for the pin-right item. Default: "Pin right" */
		pinRightLabel?: string;
		/** Label for the unpin item. Default: "Unpin" */
		unpinLabel?: string;
		/** Label for the hide-column item. Default: "Hide column" */
		hideColumnLabel?: string;
		/** Label for the reset item. Default: "Reset table" */
		resetLabel?: string;
	};
}

export interface PaginationServerOptions {
	persistSelectedOnSort?: boolean;
	persistSelectedOnPageChange?: boolean;
}

export interface ExpandableIcon {
	collapsed: React.ReactNode;
	expanded: React.ReactNode;
}

export interface ThemeIcons {
	/** Replaces the built-in CSS-arrow sort indicator on sortable columns. */
	sort?: React.ReactNode;
	/** Replaces the built-in expand/collapse chevrons on expandable rows. */
	expandable?: Partial<ExpandableIcon>;
	/** Replaces individual built-in pagination navigation icons. */
	pagination?: Partial<PaginationIcons>;
}

export type TableState<T> = {
	allSelected: boolean;
	selectedCount: number;
	selectedRows: T[];
	selectedColumn: TableColumn<T>;
	sortDirection: SortOrder;
	/** Full sort configuration in priority order. The primary entry mirrors
	 *  `selectedColumn`/`sortDirection`. Empty when nothing is sorted. */
	sortColumns: SortColumn<T>[];
	currentPage: number;
	rowsPerPage: number;
	selectedRowsFlag: boolean;
	/* server-side pagination and server-side sorting will cause selectedRows to change
	 because of this behavior onSelectedRowsChange useEffect is triggered (by design it should notify if there was a change)
	 however, when using selectableRowsSingle
	*/
	toggleOnSelectedRowsChange: boolean;
	/** True when currentPage was reset to 1 as a side-effect of SORT_CHANGE.
	 * Used to suppress the onChangePage callback so only onSort fires. */
	sortTriggeredPageReset: boolean;
};

// Theming
type ThemeText = {
	primary: string;
	secondary: string;
	disabled: string;
};

type ThemeBackground = {
	default: string;
	/** Optional separate background for column header rows. Falls back to `default`. */
	header?: string;
	/** Optional separate background for the footer row. Falls back to `default`. */
	footer?: string;
};

type ThemeContext = {
	background: string;
	text: string;
};

type ThemeDivider = {
	default: string;
};

type ThemeButton = {
	default: string;
	focus: string;
	hover: string;
	disabled: string;
};

type ThemeSelected = {
	default: string;
	text: string;
};

type ThemeHighlightOnHover = {
	default: string;
	text: string;
};

type ThemeStriped = {
	default: string;
	text: string;
};

type ThemeSpacing = {
	/** Height of body rows. Maps to --rdt-row-height. */
	rowHeight?: string;
	/** Height of the header row. Maps to --rdt-header-height. */
	headerHeight?: string;
	/** Horizontal cell padding. Maps to --rdt-cell-padding-x. */
	cellPaddingX?: string;
	/** Size of pagination nav icons. Maps to --rdt-icon-size. */
	iconSize?: string;
};

type ThemeTypography = {
	/** Body cell font size. Maps to --rdt-font-size. */
	fontSize?: string;
	/** Header cell font size. Maps to --rdt-font-size-header. */
	fontSizeHeader?: string;
	/** Font family for the whole table. Maps to --rdt-font-family. */
	fontFamily?: string;
};

type ThemeShape = {
	/** Border radius used for interactive elements (filter panel, resize handle, etc.). Maps to --rdt-border-radius. */
	borderRadius?: string;
};

export type Themes = string;
export type ThemeProp = string | Partial<Theme> | Record<string, string>;

/** Which color palette the table uses. 'system' auto-detects from prefers-color-scheme and html.dark. */
export type ColorMode = 'light' | 'dark' | 'system';

/** Color-only overrides applied when the resolved color mode is dark. */
export type DarkModeColors = {
	primary?: string;
	text?: Partial<ThemeText>;
	background?: Partial<ThemeBackground>;
	context?: Partial<ThemeContext>;
	divider?: Partial<ThemeDivider>;
	button?: Partial<ThemeButton>;
	selected?: Partial<ThemeSelected>;
	highlightOnHover?: Partial<ThemeHighlightOnHover>;
	striped?: Partial<ThemeStriped>;
};

export interface Theme {
	primary?: string;
	text: ThemeText;
	background: ThemeBackground;
	context: ThemeContext;
	divider: ThemeDivider;
	button: ThemeButton;
	selected: ThemeSelected;
	highlightOnHover: ThemeHighlightOnHover;
	striped: ThemeStriped;
	/** Sets CSS color-scheme so native form controls render correctly in the active mode. */
	colorScheme?: 'light' | 'dark';
	/** Dark-mode color overrides. Used automatically when colorMode resolves to 'dark'. */
	darkMode?: DarkModeColors;
	/** Controls row/header height and cell padding. */
	spacing?: ThemeSpacing;
	/** Controls font size and family. */
	typography?: ThemeTypography;
	/** Controls border radius on interactive elements. */
	shape?: ThemeShape;
	/** Icon overrides — replaces built-in sort, expand, and pagination icons. */
	icons?: ThemeIcons;
	/** Checkbox appearance — controls the CSS variables for the built-in CSS checkbox. */
	checkbox?: { size?: string; borderRadius?: string };
	/**
	 * Default header column separator behaviour for this theme.
	 * The `headerSeparator` prop overrides this when explicitly passed.
	 * Omitting both falls back to `'subtle'`.
	 */
	headerSeparator?: boolean | 'subtle' | 'full';
	/**
	 * Default body column separator behaviour for this theme.
	 * The `columnSeparator` prop overrides this when explicitly passed.
	 * Omitting both falls back to `false` (no separator).
	 */
	columnSeparator?: boolean | 'subtle' | 'full';
}

// Reducer Actions
export interface AllRowsAction<T> {
	type: 'SELECT_ALL_ROWS';
	keyField: string;
	rows: T[];
	rowCount: number;
	mergeSelections: boolean;
}

export interface SingleRowAction<T> {
	type: 'SELECT_SINGLE_ROW';
	keyField: string;
	row: T;
	isSelected: boolean;
	rowCount: number;
	singleSelect: boolean;
}

export interface RangeRowAction<T> {
	type: 'SELECT_RANGE';
	keyField: string;
	/** Rows in the range (inclusive of both endpoints), in visible order. */
	rangeRows: T[];
	/** Total row count for `allSelected` calculation. */
	rowCount: number;
	/** Target state for all rows in the range — derived from the anchor row's selected state. */
	select: boolean;
	/** Rows that cannot be toggled (returned by selectableRowDisabled). */
	disabledRows?: T[];
}

export interface MultiRowAction<T> {
	type: 'SELECT_MULTIPLE_ROWS';
	keyField: string;
	selectedRows: T[];
	totalRows: number;
	mergeSelections: boolean;
}

export interface SortAction<T> {
	type: 'SORT_CHANGE';
	selectedColumn: TableColumn<T>;
	clearSelectedOnSort: boolean;
	/** When true, add/update this column in the existing sort instead of replacing it. */
	additive: boolean;
	/** Direction a freshly clicked column sorts in first (from `defaultSortAsc`). */
	defaultSortDirection: SortOrder;
	/** Force a specific direction instead of cycling — used by the context menu's
	 *  explicit sort-ascending / sort-descending actions. */
	direction?: SortOrder;
}

export interface PaginationPageAction {
	type: 'CHANGE_PAGE';
	page: number;
	paginationServer: boolean;
	visibleOnly: boolean;
	persistSelectedOnPageChange: boolean;
}

export interface PaginationRowsPerPageAction {
	type: 'CHANGE_ROWS_PER_PAGE';
	rowsPerPage: number;
	page: number;
}

export interface ClearSelectedRowsAction {
	type: 'CLEAR_SELECTED_ROWS';
	selectedRowsFlag: boolean;
}

export interface ClearSortAction<T> {
	type: 'CLEAR_SORT';
	defaultSortColumn: TableColumn<T>;
	defaultSortDirection: SortOrder;
}

export type Action<T> =
	| AllRowsAction<T>
	| SingleRowAction<T>
	| RangeRowAction<T>
	| MultiRowAction<T>
	| SortAction<T>
	| PaginationPageAction
	| PaginationRowsPerPageAction
	| ClearSelectedRowsAction
	| ClearSortAction<T>;
